import { useEffect } from "react";
import { Routes, Route, Link, useLocation, useNavigate } from "react-router-dom";
import { useAuth } from "@/App";
import {
  LayoutDashboard,
  Users,
  Mail,
  FileText,
  Calendar,
  Image,
  HelpCircle,
  Gift,
  User,
  LogOut,
  Megaphone,
  LinkIcon,
  DollarSign,
  Pencil,
  History,
  Search,
  ShieldCheck,
} from "lucide-react";

import { DashboardHome } from "./sections/DashboardHome";
import { LiveEditLauncher } from "./sections/LiveEditLauncher";
import { EditHistory } from "./sections/EditHistory";
import { SeoManager } from "./sections/SeoManager";
import { SecuritySettings } from "./sections/SecuritySettings";
import { VitalsEditor } from "./sections/VitalsEditor";
import { GalleryManager } from "./sections/GalleryManager";
import { FAQManager } from "./sections/FAQManager";
import { BlogManager } from "./sections/BlogManager";
import { WishlistManager } from "./sections/WishlistManager";
import { InquiriesManagement } from "./sections/InquiriesManagement";
import { SubscribersManagement } from "./sections/SubscribersManagement";
import { TourDatesManagement } from "./sections/TourDatesManagement";
import { TravelRatesManager } from "./sections/TravelRatesManager";
import { BannerManager } from "./sections/BannerManager";
import { LinksManager } from "./sections/LinksManager";

// ============ MAIN ADMIN DASHBOARD ============
const AdminDashboard = () => {
  const { admin, logout, isAuthenticated } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();

  useEffect(() => {
    if (!isAuthenticated) {
      navigate("/admin/login");
    }
  }, [isAuthenticated, navigate]);

  const navItems = [
    { name: "Dashboard", path: "/admin/dashboard", icon: LayoutDashboard },
    { name: "Banner", path: "/admin/banner", icon: Megaphone },
    { name: "Inquiries", path: "/admin/inquiries", icon: Mail },
    { name: "Subscribers", path: "/admin/subscribers", icon: Users },
    { name: "Tour Dates", path: "/admin/tour-dates", icon: Calendar },
    { name: "Travel Rates", path: "/admin/travel-rates", icon: DollarSign },
    { name: "Live Edit", path: "/admin/live-edit", icon: Pencil },
    { name: "Edit History", path: "/admin/history", icon: History },
    { name: "SEO", path: "/admin/seo", icon: Search },
    { name: "Security", path: "/admin/security", icon: ShieldCheck },
    { name: "About/Vitals", path: "/admin/vitals", icon: User },
    { name: "Gallery", path: "/admin/gallery", icon: Image },
    { name: "FAQ", path: "/admin/faq", icon: HelpCircle },
    { name: "Blog", path: "/admin/blog", icon: FileText },
    { name: "Spoil Me", path: "/admin/wishlist", icon: Gift },
    { name: "Links", path: "/admin/links", icon: LinkIcon },
  ];

  const handleLogout = () => {
    logout();
    navigate("/admin/login");
  };

  if (!isAuthenticated) return null;

  return (
    <div className="flex min-h-screen" data-testid="admin-dashboard">
      {/* Sidebar */}
      <aside className="w-64 bg-white border-r border-sand min-h-screen p-6 flex flex-col">
        <div className="mb-8">
          <Link to="/" className="block">
            <span className="font-accent text-2xl text-champagne-gold">Kalani Luana</span>
          </Link>
          <p className="font-body text-xs text-taupe mt-1">Admin Panel</p>
        </div>

        <nav className="space-y-1 flex-1">
          {navItems.map((item) => (
            <Link
              key={item.path}
              to={item.path}
              className={`flex items-center gap-3 px-4 py-3 rounded-lg font-body text-sm transition-all ${
                location.pathname === item.path
                  ? 'bg-blush-pink text-jade-text font-medium'
                  : 'text-stone-grey hover:bg-sand/50'
              }`}
            >
              <item.icon className="w-5 h-5" />
              {item.name}
            </Link>
          ))}
        </nav>

        <div className="pt-6 border-t border-sand">
          <div className="mb-4">
            <p className="font-body text-sm text-charcoal">{admin?.name}</p>
            <p className="font-body text-xs text-taupe">{admin?.email}</p>
          </div>
          <button
            onClick={handleLogout}
            className="flex items-center gap-2 text-stone-grey hover:text-lipstick-red transition-colors font-body text-sm"
          >
            <LogOut className="w-4 h-4" />
            Sign Out
          </button>
        </div>
      </aside>

      {/* Main Content */}
      <main className="flex-1 p-8 bg-ivory overflow-auto">
        <Routes>
          <Route path="dashboard" element={<DashboardHome />} />
          <Route path="banner" element={<BannerManager />} />
          <Route path="inquiries" element={<InquiriesManagement />} />
          <Route path="subscribers" element={<SubscribersManagement />} />
          <Route path="tour-dates" element={<TourDatesManagement />} />
          <Route path="travel-rates" element={<TravelRatesManager />} />
          <Route path="live-edit" element={<LiveEditLauncher />} />
          <Route path="history" element={<EditHistory />} />
          <Route path="seo" element={<SeoManager />} />
          <Route path="security" element={<SecuritySettings />} />
          <Route path="vitals" element={<VitalsEditor />} />
          <Route path="gallery" element={<GalleryManager />} />
          <Route path="faq" element={<FAQManager />} />
          <Route path="blog" element={<BlogManager />} />
          <Route path="wishlist" element={<WishlistManager />} />
          <Route path="links" element={<LinksManager />} />
          <Route path="*" element={<DashboardHome />} />
        </Routes>
      </main>
    </div>
  );
};

export default AdminDashboard;
